import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon, FlaskConicalIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  ErrorNotice,
  Field,
  LoadingState,
  Panel,
  Pill,
  TextInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  CARD_SURCHARGE_MAX_PERCENT,
  readCardSurcharge,
  saveCardSurcharge,
  readStripeConfig,
  saveStripeConfig,
  clearStripeConfig,
  readDocusealConfig,
  saveDocusealConfig,
  clearDocusealConfig,
} from '@/lib/contracts';
import {
  createIntegrationConnectUrl,
  disconnectIntegration,
  importSalesforceOpportunity,
  readIntegrationStatus,
  readSfOpportunityImportEnabled,
  type IntegrationProvider,
  type OpportunityImportResult,
} from '@/lib/events';
import googleLogo from '@/assets/integrations/google.svg';
import salesforceLogo from '@/assets/integrations/salesforce.svg';
import quickbooksLogo from '@/assets/integrations/quickbooks.svg';
import stripeLogo from '@/assets/integrations/stripe.svg';
import docusealLogo from '@/assets/integrations/docuseal.svg';

/** What each integration is called and what it powers, for the cards. */
const PROVIDER_META: Record<
  IntegrationProvider,
  { name: string; description: string; icon: React.ReactElement }
> = {
  google: {
    name: 'Google',
    description:
      'Sends platform email through Gmail and streams event attachments and avatars from Google Drive.',
    icon: <img src={googleLogo} alt="" className="h-6 w-6 object-contain" />,
  },
  salesforce: {
    name: 'Salesforce',
    description:
      'Backs the event and program syncs — contracts, accounts, contacts, and program content flow from Salesforce.',
    icon: <img src={salesforceLogo} alt="" className="h-6 w-6 object-contain" />,
  },
  quickbooks: {
    name: 'QuickBooks',
    description:
      'Syncs expense transactions and the chart of accounts hourly, powering the finance (expenses) section of the analyst reports.',
    icon: <img src={quickbooksLogo} alt="" className="h-6 w-6 object-contain" />,
  },
};

/**
 * The Integrations tab of Settings: connection status plus
 * connect/disconnect for the platform's Google and Salesforce credentials.
 * Admin-only (the API enforces it; the nav hides Settings for everyone else).
 *
 * @returns The integrations tab.
 */
export function IntegrationsTab(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  // OAuth landings: ?tab=integrations&connected=<provider> or &error=<msg>.
  const connected = params.get('connected');
  const oauthError = params.get('error');

  function clearBanner(): void {
    const next = new URLSearchParams();
    next.set('tab', 'integrations');
    setParams(next, { replace: true });
  }

  return (
    <div className="mt-6">
      {connected ? (
        <div className="mb-4 flex items-center justify-between rounded-portal-button border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-portal-small font-bold text-emerald-800">
            {PROVIDER_META[connected as IntegrationProvider]?.name ?? connected} connected.
          </p>
          <button
            type="button"
            onClick={clearBanner}
            className="text-portal-micro font-bold text-emerald-700 hover:text-emerald-900"
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {oauthError ? (
        <div className="mb-4">
          <ErrorNotice inline title="Connection failed" message={oauthError} />
        </div>
      ) : null}

      {/* No items-start: rows stretch so both cards share the tallest height. */}
      <div className="grid gap-4 xl:grid-cols-2">
        <IntegrationCard provider="google" />
        <IntegrationCard provider="salesforce" />
        <IntegrationCard provider="quickbooks" />
        <StripeCard />
        <DocusealCard />
        <SalesforceImportCard />
      </div>
    </div>
  );
}

/**
 * Staging-only tool: import one Salesforce opportunity by Opportunity Id or
 * contract number — including opportunities that have no contract yet (the
 * API bypasses its creation guards for this endpoint). Renders nothing
 * unless the API's public config enables `FEATURE_SF_OPPORTUNITY_IMPORT`,
 * so the card simply does not exist outside staging.
 */
function SalesforceImportCard(): React.ReactElement | null {
  const [reference, setReference] = useState('');

  const enabled = useQuery({
    queryKey: ['sf-opportunity-import-enabled'],
    queryFn: ({ signal }) => readSfOpportunityImportEnabled(signal),
    staleTime: Infinity,
  });

  const doImport = useMutation<OpportunityImportResult, unknown, string>({
    mutationFn: (ref) => importSalesforceOpportunity(ref),
  });

  if (enabled.data !== true) return null;

  const result = doImport.data;

  return (
    <Panel className="flex h-full flex-col p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-portal-button bg-portal-bg text-portal-ink">
            <FlaskConicalIcon aria-hidden className="size-5" />
          </span>
          <div>
            <p className="text-portal-body font-bold text-portal-ink">
              Salesforce opportunity import
            </p>
            <p className="text-portal-small text-portal-muted">
              Pull one opportunity into the portal by Id (006…) or contract
              number (9363-BC) — even before it has a contract.
            </p>
          </div>
        </div>
        <Pill tone="warning">Staging</Pill>
      </div>

      <form
        className="mt-4 flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (reference.trim() && !doImport.isPending) doImport.mutate(reference.trim());
        }}
      >
        <div className="flex-1">
          <Field label="Opportunity Id or contract number" htmlFor="sf-import-reference">
            <TextInput
              id="sf-import-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="006… or 9363-BC"
            />
          </Field>
        </div>
        <Button type="submit" disabled={doImport.isPending || !reference.trim()}>
          {doImport.isPending ? 'Importing…' : 'Import'}
        </Button>
      </form>

      {doImport.isError ? (
        <div className="mt-3">
          <ErrorNotice inline title="Import failed" message={errorMessage(doImport.error)} />
        </div>
      ) : null}

      {result ? (
        <p className="mt-3 text-portal-small text-portal-ink" role="status">
          {result.status === 'skipped' ? (
            <>Skipped: {result.reason ?? 'no reason given'}</>
          ) : (
            <>
              {result.status === 'created' ? 'Created' : 'Updated'}{' '}
              <strong>{result.opportunityName ?? result.opportunityId}</strong>
              {result.contractNumber ? ` (#${result.contractNumber})` : ''}
              {result.eventId ? (
                <Link
                  to={`/events/${result.eventId}`}
                  className="ml-2 inline-flex items-center gap-1 font-bold text-portal-accent hover:underline"
                >
                  Open event <ArrowRightIcon aria-hidden className="size-3.5" />
                </Link>
              ) : null}
            </>
          )}
        </p>
      ) : null}
    </Panel>
  );
}

/** One integration's card: status, connect, disconnect. */
function IntegrationCard({
  provider,
}: {
  readonly provider: IntegrationProvider;
}): React.ReactElement {
  const meta = PROVIDER_META[provider];
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const status = useQuery({
    queryKey: ['integration-status', provider],
    queryFn: ({ signal }) => readIntegrationStatus(provider, signal),
  });

  const connect = useMutation({
    mutationFn: () => createIntegrationConnectUrl(provider),
    onSuccess: (url) => {
      window.location.href = url;
    },
  });

  const disconnect = useMutation({
    mutationFn: () => disconnectIntegration(provider),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: ['integration-status', provider] });
    },
  });

  const isConnected = status.data?.status === 'connected';

  return (
    // Full-height flex column so the action row pins to the bottom and both
    // cards in the grid read as the same size.
    <Panel className="flex h-full flex-col p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-portal-button bg-portal-bg text-portal-ink">
            {meta.icon}
          </span>
          <div>
            <h2 className="text-portal-card-title font-bold">{meta.name}</h2>
            {status.isPending ? null : (
              <Pill tone={isConnected ? 'success' : 'neutral'}>
                {isConnected ? 'Connected' : 'Not connected'}
              </Pill>
            )}
          </div>
        </div>
      </div>

      <p className="mt-3 text-portal-small text-portal-muted">{meta.description}</p>

      {status.isPending ? (
        <div className="mt-auto pt-4">
          <LoadingState label="Checking connection…" />
        </div>
      ) : status.isError ? (
        <div className="mt-auto pt-4">
          <ErrorNotice inline title="Status unavailable" message={errorMessage(status.error)} />
        </div>
      ) : (
        <div className="mt-auto flex items-center gap-2 pt-4">
          {isConnected ? (
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              Disconnect
            </Button>
          ) : (
            <Button onClick={() => connect.mutate()} disabled={connect.isPending}>
              {connect.isPending ? 'Redirecting…' : `Connect ${meta.name}`}
            </Button>
          )}
        </div>
      )}
      {connect.isError ? (
        <div className="mt-3">
          <ErrorNotice inline title="Could not start connect" message={errorMessage(connect.error)} />
        </div>
      ) : null}

      <ConfirmDialog
        open={confirming}
        tone="danger"
        title={`Disconnect ${meta.name}?`}
        message={`Everything that depends on the ${meta.name} connection stops working until it is reconnected.`}
        confirmLabel="Disconnect"
        onConfirm={() => disconnect.mutate()}
        onCancel={() => {
          if (!disconnect.isPending) setConfirming(false);
        }}
        pending={disconnect.isPending}
        error={disconnect.isError ? errorMessage(disconnect.error) : undefined}
      />
    </Panel>
  );
}


/**
 * The Stripe account behind in-portal event payments. Keys are stored
 * encrypted server-side and are write-only — the card shows only the
 * source and the secret key's last four. Clearing falls back to the
 * environment-configured account, if any.
 */
function StripeCard(): React.ReactElement {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['stripe-config'],
    queryFn: ({ signal }) => readStripeConfig(signal),
  });
  const [secretKey, setSecretKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['stripe-config'] });
  const save = useMutation({
    mutationFn: () =>
      saveStripeConfig({
        secretKey: secretKey.trim(),
        ...(publishableKey.trim() ? { publishableKey: publishableKey.trim() } : {}),
        ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
      }),
    onSuccess: () => {
      setSecretKey('');
      setWebhookSecret('');
      refresh();
    },
  });
  const clear = useMutation({ mutationFn: clearStripeConfig, onSuccess: refresh });
  const data = status.data;

  return (
    <Panel className="flex h-full flex-col p-5">
      <div className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-portal-button bg-portal-bg">
          <img src={stripeLogo} alt="" className="h-6 w-6 object-contain" />
        </span>
        <h2 className="text-portal-card-title font-bold">Stripe payments</h2>
      </div>
      <p className="mt-3 text-portal-micro text-portal-muted">
        The Stripe account clients pay into from My Events. Keys are stored encrypted and
        never shown again — only the last four digits.
      </p>
      <p className="mt-3 text-portal-small">
        {data?.source === 'settings'
          ? `Configured here · secret key ····${data.secretKeyLast4}`
          : data?.source === 'env'
            ? `Configured by the environment · secret key ····${data.secretKeyLast4}`
            : 'Not configured — in-portal payments are hidden.'}
      </p>
      <div className="mt-3 space-y-2">
        <input
          type="password"
          autoComplete="off"
          placeholder="Secret key (sk_…)"
          aria-label="Stripe secret key"
          value={secretKey}
          onChange={(event) => setSecretKey(event.target.value)}
          className="w-full rounded-portal-button border bg-white px-3 py-2 text-portal-small"
        />
        <input
          type="text"
          autoComplete="off"
          placeholder="Publishable key (pk_…)"
          aria-label="Stripe publishable key"
          value={publishableKey}
          onChange={(event) => setPublishableKey(event.target.value)}
          className="w-full rounded-portal-button border bg-white px-3 py-2 text-portal-small"
        />
        <input
          type="password"
          autoComplete="off"
          placeholder="Webhook signing secret (whsec_…) — optional"
          aria-label="Stripe webhook secret"
          value={webhookSecret}
          onChange={(event) => setWebhookSecret(event.target.value)}
          className="w-full rounded-portal-button border bg-white px-3 py-2 text-portal-small"
        />
      </div>
      {save.isError ? (
        <div className="mt-2">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}
      <div className="mt-3 flex gap-2">
        <Button size="compact" disabled={secretKey.trim() === '' || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save keys'}
        </Button>
        {data?.source === 'settings' ? (
          <Button size="compact" variant="secondary" disabled={clear.isPending} onClick={() => clear.mutate()}>
            Clear stored keys
          </Button>
        ) : null}
      </div>
      <CardSurchargeSetting />
    </Panel>
  );
}

/**
 * The credit-card surcharge: a percentage added to credit card payments only,
 * shown to the payer before they pay. Off until accounting has done the
 * compliance steps; capped at Visa's 3%.
 */
function CardSurchargeSetting(): React.ReactElement {
  const queryClient = useQueryClient();
  const current = useQuery({ queryKey: ['card-surcharge'], queryFn: ({ signal }) => readCardSurcharge(signal) });
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? String(current.data?.percent ?? 0);
  const parsed = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= CARD_SURCHARGE_MAX_PERCENT;
  const save = useMutation({
    mutationFn: () => saveCardSurcharge(parsed),
    onSuccess: (saved) => {
      setDraft(null);
      queryClient.setQueryData(['card-surcharge'], saved);
    },
  });
  const percent = current.data?.percent ?? 0;

  return (
    <div className="mt-5 border-t border-portal-line pt-4">
      <Field
        label="Credit card surcharge (%)"
        htmlFor="card-surcharge"
        hint={`Added to credit card payments only, never bank, debit or prepaid, and shown to the payer before they pay. Up to ${CARD_SURCHARGE_MAX_PERCENT}%; 0 turns it off. Turn it on only after accounting has notified the card networks and checked state rules.`}
      >
        <div className="flex items-center gap-2">
          <TextInput
            id="card-surcharge"
            inputMode="decimal"
            className="w-24"
            value={value}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button size="compact" disabled={!valid || draft === null || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Field>
      <p className="mt-2 text-portal-micro text-portal-muted">
        {percent > 0 ? `On: credit cards pay ${percent}% more.` : 'Off: no card payment carries a fee.'}
      </p>
      {!valid ? (
        <p className="mt-1 text-portal-micro text-portal-danger">Enter a number from 0 to {CARD_SURCHARGE_MAX_PERCENT}.</p>
      ) : null}
      {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
    </div>
  );
}

/** The DocuSeal account behind contract e-sign (write-only key storage). */
function DocusealCard(): React.ReactElement {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['docuseal-config'],
    queryFn: ({ signal }) => readDocusealConfig(signal),
  });
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['docuseal-config'] });
  const save = useMutation({
    mutationFn: () =>
      saveDocusealConfig({
        apiKey: apiKey.trim(),
        ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
      }),
    onSuccess: () => {
      setApiKey('');
      setWebhookSecret('');
      refresh();
    },
  });
  const clear = useMutation({ mutationFn: clearDocusealConfig, onSuccess: refresh });
  const data = status.data;

  return (
    <Panel className="flex h-full flex-col p-5">
      <div className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-portal-button bg-portal-bg">
          <img src={docusealLogo} alt="" className="h-6 w-6 object-contain" />
        </span>
        <h2 className="text-portal-card-title font-bold">DocuSeal e-sign</h2>
      </div>
      <p className="mt-3 text-portal-micro text-portal-muted">
        The DocuSeal account contracts are signed through. The API key is stored encrypted
        and never shown again — only the last four characters. The webhook secret must
        match the x-docuseal-secret header configured in DocuSeal.
      </p>
      <p className="mt-3 text-portal-small">
        {data?.source === 'settings'
          ? `Configured here · API key ····${data.apiKeyLast4}${data.webhookSecretSet ? ' · webhook secret set' : ' · no webhook secret'}`
          : data?.source === 'env'
            ? `Configured by the environment · API key ····${data.apiKeyLast4}`
            : 'Not configured — contract generation and signing are unavailable.'}
      </p>
      <div className="mt-3 space-y-2">
        <input
          type="password"
          autoComplete="off"
          placeholder="DocuSeal API key"
          aria-label="DocuSeal API key"
          value={apiKey}
          onChange={(event) => setApiKey(event.target.value)}
          className="w-full rounded-portal-button border bg-white px-3 py-2 text-portal-small"
        />
        <input
          type="password"
          autoComplete="off"
          placeholder="Webhook shared secret — optional"
          aria-label="DocuSeal webhook secret"
          value={webhookSecret}
          onChange={(event) => setWebhookSecret(event.target.value)}
          className="w-full rounded-portal-button border bg-white px-3 py-2 text-portal-small"
        />
      </div>
      {save.isError ? (
        <div className="mt-2">
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} />
        </div>
      ) : null}
      <div className="mt-3 flex gap-2">
        <Button size="compact" disabled={apiKey.trim() === '' || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save key'}
        </Button>
        {data?.source === 'settings' ? (
          <Button size="compact" variant="secondary" disabled={clear.isPending} onClick={() => clear.mutate()}>
            Clear stored key
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}
